import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Gavel, Sparkles, Clock, TrendingDown, Users, ChevronRight, CheckCircle2, X, Briefcase } from 'lucide-react';
import { createBidding, findOpenBidding, addBid, closeBidding, acceptBid, useActivity } from '../lib/activityStore';
import type { ServiceId } from '../lib/serviceData';
interface BidPreview {
  id: number;
  supplier: string;
  price: number;
  rating: number;
  initials: string;
  color: string;
}
const mockBids: BidPreview[] = [{
  id: 1,
  supplier: 'Al Khaleej Movers',
  price: 720,
  rating: 4.6,
  initials: 'AK',
  color: 'bg-amber-100 text-amber-700'
}, {
  id: 2,
  supplier: 'BlueLine Express',
  price: 690,
  rating: 4.8,
  initials: 'BL',
  color: 'bg-blue-100 text-blue-700'
}, {
  id: 3,
  supplier: 'GulfCargo Solutions',
  price: 750,
  rating: 4.5,
  initials: 'GC',
  color: 'bg-purple-100 text-purple-700'
}, {
  id: 4,
  supplier: 'Sahara Logistics',
  price: 680,
  rating: 4.7,
  initials: 'SL',
  color: 'bg-rose-100 text-rose-700'
}];
interface BiddingPanelProps {
  serviceId: ServiceId;
  serviceName: string;
  prompt?: string;
  routeHeadline: string;
  pickup?: string;
  dropoff?: string;
  estimatedPrice: number;
  photos?: string[];
}
export function BiddingPanel({
  serviceId,
  serviceName,
  prompt,
  routeHeadline,
  pickup,
  dropoff,
  estimatedPrice,
  photos
}: BiddingPanelProps) {
  const navigate = useNavigate();
  // Re-render when the activity store changes (e.g. a bid is added).
  const {
    jobs
  } = useActivity();
  // Find existing open bidding for this service (so the user can resume after navigating away).
  const existingBidding = useMemo(() => jobs.find(j => j.type === 'bidding' && j.status === 'bidding_open' && j.serviceId === serviceId), [jobs, serviceId]);
  const opened = !!existingBidding;
  const job = existingBidding;
  const bidCount = job?.bids?.length || 0;
  // Time left
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!opened) return;
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, [opened]);
  const msLeft = job?.closesAt ? Math.max(0, job.closesAt - now) : 0;
  const hoursLeft = Math.floor(msLeft / (60 * 60 * 1000));
  const minutesLeft = Math.floor(msLeft % (60 * 60 * 1000) / (60 * 1000));
  // Simulate bids streaming in over time once bidding is opened.
  useEffect(() => {
    if (!job) return;
    if ((job.bids?.length || 0) >= mockBids.length) return;
    const timer = setInterval(() => {
      const fresh = findOpenBidding(serviceId);
      if (!fresh) return;
      const nextIdx = fresh.bids?.length || 0;
      if (nextIdx >= mockBids.length) {
        clearInterval(timer);
        return;
      }
      const b = mockBids[nextIdx];
      addBid(fresh.id, {
        supplier: b.supplier,
        price: b.price,
        rating: b.rating,
        initials: b.initials,
        color: b.color,
        receivedAt: Date.now()
      });
    }, 2200);
    return () => clearInterval(timer);
  }, [job?.id, serviceId]);
  const handleOpen = () => {
    createBidding({
      serviceId,
      serviceName,
      prompt: prompt || '',
      routeHeadline,
      pickup,
      dropoff,
      estimatedPrice,
      photos
    });
  };
  const handleClose = () => {
    if (job) closeBidding(job.id);
  };
  const visibleBids = job?.bids || [];
  const bestBid = visibleBids.length ? visibleBids.reduce((a, b) => a.price < b.price ? a : b) : null;
  const savings = bestBid ? Math.max(0, estimatedPrice - bestBid.price) : 0;
  if (!opened) {
    return <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm" data-id="element-202">
        <div className="absolute top-0 left-0 right-0 h-1 orange-gradient-bg" data-id="element-203" />
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-brand-100 rounded-full blur-3xl opacity-50 pointer-events-none" data-id="element-204" />

        <div className="p-3.5 flex items-center gap-3 relative" data-id="element-205">
          <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center shrink-0" data-id="element-206">
            <Gavel className="w-4 h-4 text-white" data-id="element-207" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-208">
            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap" data-id="element-209">
              <h3 className="font-bold text-gray-900 text-sm" data-id="element-210">
                Open for bidding
              </h3>
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-full" data-id="element-211">
                <TrendingDown className="w-2.5 h-2.5" data-id="element-212" />
                Save up to 22%
              </span>
            </div>
            <p className="text-[11px] text-gray-500 leading-snug" data-id="element-213">
              Carriers compete for your job · ~8 bids · 24h window
            </p>
          </div>
          <button onClick={handleOpen} className="shrink-0 px-3.5 py-2 rounded-xl bg-gray-900 hover:bg-black text-white font-semibold text-xs flex items-center gap-1 shadow-md transition-colors" data-id="element-214">
            Open
            <ChevronRight className="w-3.5 h-3.5" data-id="element-215" />
          </button>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} className="relative overflow-hidden rounded-3xl bg-gray-900 text-white shadow-xl" data-id="element-216">
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-brand-500 rounded-full blur-3xl opacity-30 pointer-events-none" data-id="element-217" />
      <div className="absolute top-0 left-0 right-0 h-1 orange-gradient-bg" data-id="element-218" />

      <div className="p-5 relative" data-id="element-219">
        <div className="flex items-start justify-between mb-4" data-id="element-220">
          <div className="flex items-center gap-3" data-id="element-221">
            <div className="w-10 h-10 rounded-xl orange-gradient-bg flex items-center justify-center shadow-md" data-id="element-222">
              <Gavel className="w-5 h-5 text-white" data-id="element-223" />
            </div>
            <div data-id="element-224">
              <div className="flex items-center gap-2 mb-0.5" data-id="element-225">
                <h3 className="font-bold" data-id="element-226">Bidding active</h3>
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-green-300 bg-green-500/15 border border-green-500/30 px-2 py-0.5 rounded-full" data-id="element-227">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" data-id="element-228" />
                  Live
                </span>
              </div>
              <p className="text-xs text-gray-400" data-id="element-229">
                {serviceName} request • {bidCount} bid
                {bidCount === 1 ? '' : 's'} received
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors" aria-label="Close bidding" title="Close bidding" data-id="element-230">
            <X className="w-4 h-4" data-id="element-231" />
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-232">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10" data-id="element-233">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium" data-id="element-234">
              Bids
            </p>
            <p className="text-lg font-display font-bold text-white" data-id="element-235">
              {bidCount}
            </p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10" data-id="element-236">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium" data-id="element-237">
              Best price
            </p>
            <p className="text-lg font-display font-bold text-white" data-id="element-238">
              {bestBid ? `AED ${bestBid.price}` : '—'}
            </p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10" data-id="element-239">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium" data-id="element-240">
              Closes in
            </p>
            <p className="text-lg font-display font-bold text-white" data-id="element-241">
              {String(hoursLeft).padStart(2, '0')}h{' '}
              {String(minutesLeft).padStart(2, '0')}m
            </p>
          </div>
        </div>

        {/* Savings call-out */}
        {bestBid && savings > 0 && <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-3 py-2 mb-4 flex items-center gap-2" data-id="element-242">
            <TrendingDown className="w-4 h-4 text-green-400" data-id="element-243" />
            <p className="text-xs text-green-200" data-id="element-244">
              <span className="font-bold text-green-300" data-id="element-245">
                Saving AED {savings}
              </span>{' '}
              vs. the AI estimate so far.
            </p>
          </div>}

        {/* Live bid feed */}
        <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-id="element-246">
          <AnimatePresence initial={false} data-id="element-247">
            {visibleBids.length === 0 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-xs text-gray-400 text-center py-6" data-id="element-248">
                Notifying carrier network… first bids usually arrive in 1–3
                minutes.
              </motion.div>}
            {visibleBids.map((bid, i) => {
            const isBest = bestBid?.supplier === bid.supplier;
            return <motion.div key={`${bid.supplier}-${i}`} initial={{
              opacity: 0,
              y: 12,
              scale: 0.96
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              delay: i * 0.05,
              type: 'spring',
              damping: 22
            }} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors ${isBest ? 'bg-brand-500/15 border-brand-400/40' : 'bg-white/5 border-white/10'}`} data-id="element-249">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${bid.color}`} data-id="element-250">
                    {bid.initials}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-251">
                    <div className="flex items-center gap-2" data-id="element-252">
                      <p className="text-sm font-semibold text-white truncate" data-id="element-253">
                        {bid.supplier}
                      </p>
                      {isBest && <span className="text-[9px] font-bold uppercase tracking-wider bg-brand-500 text-white px-1.5 py-0.5 rounded" data-id="element-254">
                          Best
                        </span>}
                    </div>
                    <p className="text-[11px] text-gray-400" data-id="element-255">
                      {bid.rating} ★ • just submitted a bid
                    </p>
                  </div>
                  <div className="text-right shrink-0" data-id="element-256">
                    <p className="font-display font-bold text-white" data-id="element-257">
                      AED {bid.price}
                    </p>
                    <button onClick={() => {
                  if (!job) return;
                  const updated = acceptBid(job.id, i);
                  if (!updated) return;
                  navigate('/payment', {
                    state: {
                      service: serviceId,
                      prompt,
                      jobId: updated.id,
                      photos: updated.photos || photos,
                      supplier: {
                        name: bid.supplier,
                        price: bid.price,
                        truck: bid.truck || 'Carrier-selected truck'
                      }
                    }
                  });
                }} className="text-[11px] font-semibold text-brand-300 hover:text-brand-200" data-id="element-258">
                      Accept →
                    </button>
                  </div>
                </motion.div>;
          })}
          </AnimatePresence>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 text-[11px] text-gray-400" data-id="element-259">
          <span className="flex items-center gap-1.5" data-id="element-260">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-400" data-id="element-261" />
            You can still book instantly from the list below.
          </span>
          <button onClick={() => navigate('/activity')} className="flex items-center gap-1 text-brand-300 hover:text-brand-200 font-semibold" data-id="element-262">
            <Briefcase className="w-3 h-3" data-id="element-263" /> View in My Jobs
          </button>
        </div>
      </div>
    </motion.div>;
}